import React, { useMemo } from 'react';
import { Box, costFormater, Text } from '@common-fe/common-fe';

import { ServicePaymentType } from '@/modules/HealthPlan/HealthPlan.types';
import { PAYOUT_RULE_OPTIONS } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/TierItem/tierItem.types';
import { RenewalDeductibleConfig, RenewalProcessingTier } from '@/modules/Renewals/renewals.types';
import { getTierTriggerNames } from '@/modules/Renewals/RenewalSetup/utils/getTierTriggerNames';
import { isEmbeddedOnlyTier } from '@/modules/Renewals/RenewalSetup/utils/isEmbeddedOnlyTier';

import {
  AMOUNT_NOT_SPECIFIED,
  APPLIED_TO_DEDUCTIBLE,
  LOCK_CARD_PURSE_STATUS,
} from '../HealthPlanSummaryCard.constants';
import { PayoutConnectorLine, VerticalLine } from '../HealthPlanSummaryCard.styles';

import ExpandableRow from './ExpandableRow';
import TierTitle from './TierTitle';

interface Props {
  tier: RenewalProcessingTier;
  index: number;
  isLast: boolean;
  testId?: string;
  deductibleConfigs?: RenewalDeductibleConfig[];
}

const TierSection: React.FC<Props> = ({ tier, index, isLast, testId, deductibleConfigs }) => {
  const hasLockCardPurse = (tier.cardPurseLockPlanNames || []).length > 0;
  const hasServices = (tier.serviceConfigs || []).length > 0;
  const isEmbeddedTier = useMemo(
    () => isEmbeddedOnlyTier(tier, deductibleConfigs),
    [tier, deductibleConfigs]
  );
  const triggerNames = useMemo(() => getTierTriggerNames(tier, deductibleConfigs), [tier, deductibleConfigs]);

  const shouldHideLockCardPurseHeading = (isLast && !hasLockCardPurse) || isEmbeddedTier;
  const isLockCardPurseVisible = !(shouldHideLockCardPurseHeading && !hasServices);
  const payoutRuleLabel = PAYOUT_RULE_OPTIONS.find((opt) => opt.key === tier.payoutRuleType)?.value || '';

  const paymentRulesContent = useMemo(
    () =>
      (tier.serviceConfigs || []).map((service) => ({
        servicesName: (service.services || [])
          .filter((s) => s.name)
          .map((s) => s.name)
          .join(', '),
        claimValue: service.claimValue,
        isAppliedDeductible: service.isAppliedDeductible,
        rules: (service.paymentRules || []).map((rule, ruleIdx) => {
          const isAmount = service.paymentType === ServicePaymentType.AMOUNT;
          const formattedValue = isAmount
            ? costFormater(rule.maxAmount || 0, true)
            : `${Math.floor((rule.percentage || 0) * 100)}%`;

          return {
            planId: rule.planId,
            label: `Payment Rule ${ruleIdx + 1}: `,
            value: `${rule.planName} (${formattedValue})`,
          };
        }),
      })),
    [tier.serviceConfigs]
  );

  const servicesContent = (
    <Box style={{ position: 'relative' }}>
      <Box>
        <Text size="small" color="textSecondary" weight={500} margin={{ bottom: 'spacing4' }}>
          Services
        </Text>
        <Box gap="spacing8">
          {paymentRulesContent.map((service) => (
            <Box
              key={`${tier.name}-${service.servicesName}-${service.claimValue}`}
              pad={{ vertical: 'spacing12', horizontal: 'spacing16' }}
              border={{ size: 'small', color: 'border1' }}
              round="container2Round"
              background="canvas"
            >
              <Box gap="spacing2" justify="center">
                <Text size="medium">
                  <Text weight={700}>
                    {service.servicesName}
                    {service.servicesName ? ': ' : ''}
                  </Text>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {service.claimValue != null
                      ? costFormater(service.claimValue, true)
                      : AMOUNT_NOT_SPECIFIED}
                    {service.isAppliedDeductible ? APPLIED_TO_DEDUCTIBLE : ', Not applied to deductible'}{' '}
                    <Box
                      as="span"
                      width="8px"
                      height="8px"
                      round="full"
                      background={service.isAppliedDeductible ? 'success' : 'warning'}
                      style={{ display: 'inline-block', verticalAlign: 'middle' }}
                    />
                  </span>
                </Text>
                {service.rules.map((rule) => (
                  <Text key={rule.planId} color="textBody">
                    <Text weight={700} color="textBody">
                      {rule.label}
                    </Text>
                    {rule.value}
                  </Text>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <ExpandableRow
      titleNode={<TierTitle tier={tier} tierIndex={index} />}
      defaultExpanded={false}
      isLast={isLast}
      testId={`${testId}_tier-${index}`}
      showArrowDown={!isLast}
      allowBottomMargin
    >
      {/* Blocks with connecting lines overlaid */}
      <Box style={{ position: 'relative' }}>
        {/* Vertical line 1: from Trigger → Payout rules (only when triggers exist) */}
        {triggerNames.length > 0 && <VerticalLine />}

        {/* Block 1: Trigger (shown only when the tier has triggers) */}
        {triggerNames.length > 0 && (
          <Box
            background="module"
            round="container2Round"
            pad={{ vertical: 'spacing16', horizontal: 'spacing16' }}
            margin={{ bottom: 'spacing8' }}
            style={{ position: 'relative' }}
          >
            <PayoutConnectorLine />
            <ExpandableRow
              testId={`${testId}_tier-${index}_triggers`}
              title="Triggers:"
              titleValue={triggerNames.join(' or ')}
              defaultExpanded
              disabled
            />
          </Box>
        )}

        {/* Block 2: Payout rules */}
        <Box
          background="module"
          round="container2Round"
          pad={{ vertical: 'spacing16', horizontal: 'spacing16' }}
          style={{ position: 'relative' }}
        >
          {/* VerticalLine from Payout rules → Lock card purse whenever Lock card purse is visible */}
          {isLockCardPurseVisible && <VerticalLine />}
          {/* PayoutConnectorLine on Payout rules only when it is the first block (no triggers) */}
          {triggerNames.length === 0 && <PayoutConnectorLine />}
          <ExpandableRow
            testId={`${testId}_tier-${index}_payout-rules`}
            title="Payout rules:"
            titleValue={payoutRuleLabel}
            defaultExpanded
            disabled
          />
        </Box>

        {/* Block 3: Lock card purse. Rendered only when the heading is visible
            or there are services; otherwise omitted entirely (no empty block,
            no connector line) — e.g. an embedded tier with no services. */}
        {isLockCardPurseVisible && (
          <Box
            background="module"
            round="container2Round"
            pad={{ vertical: 'spacing16', horizontal: 'spacing16' }}
            margin={{ top: 'spacing8' }}
            style={{ position: 'relative' }}
          >
            <ExpandableRow
              testId={`${testId}_tier-${index}_lock-card-purse`}
              title={shouldHideLockCardPurseHeading ? '' : 'Lock card purse:'}
              titleValue={
                shouldHideLockCardPurseHeading
                  ? ''
                  : hasLockCardPurse
                    ? LOCK_CARD_PURSE_STATUS.LOCK
                    : LOCK_CARD_PURSE_STATUS.UNLOCK
              }
              subtitle={hasLockCardPurse ? (tier.cardPurseLockPlanNames || []).join(', ') : undefined}
              defaultExpanded={hasServices}
              disabled={!hasServices}
              hideChildren={!hasServices}
              contentLines={hasLockCardPurse ? 2 : 1}
              shouldRenderCurvedLine
            >
              {servicesContent}
            </ExpandableRow>
          </Box>
        )}
      </Box>
    </ExpandableRow>
  );
};

export default TierSection;
